/**
 * Hook TanStack puro para busca de detalhes do XML na API Qive
 * Não faz side effects, não popula store, não lida com histórico.
 * Apenas executa a requisição e retorna o resultado.
 *
 * @example
 * const { mutate, mutateAsync, data, isLoading, error } = useFetchXmlDetailsTanstack();
 * mutate("chaveNFe");
 */

import { useMutation } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import {
  OrigemProduto,
  Produto,
  XmlDetailsResponse,
  XmlItem,
} from "@PN/inclusao";

// =================================================================
// TIPOS DE ERRO
// =================================================================

interface ApiError {
  message: string;
  type: string;
  code?: string;
  details?: any;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  warning?: string;
}

const DUPLICATE_KEY_MESSAGE =
  "Esta chave já foi dada entrada anteriormente no sistema";

// =================================================================
// FUNÇÕES DE CONVERSÃO
// =================================================================

/**
 * Converte XmlItem para Produto
 */
export function converterXmlItemParaProdutoInclusao(
  item: XmlItem,
  index: number
): Produto {
  const quantidadeOriginal = Number(item.quantidade?.replace(",", ".")) || 0;

  return {
    // === CAMPOS OBRIGATÓRIOS ===
    idUnico: `xml_${Date.now()}_${index + 1}`,
    quantidade: quantidadeOriginal,
    valorTotal: Number(item.valorTotal?.replace(",", ".")) || 0,
    origem: OrigemProduto.XML,

    // === VALORES DETALHADOS (XML) ===
    valorUnitario: Number(item.valorUnitario?.replace(",", ".")) || undefined,
    valorUnitarioTrib:
      Number(item.valorUnitarioTrib?.replace(",", ".")) || undefined,
    valorDesconto: Number(item.valorDesconto?.replace(",", ".")) || undefined,
    valorFrete: Number(item.valorFrete?.replace(",", ".")) || undefined,
    valorSeguro: Number(item.valorSeguro?.replace(",", ".")) || undefined,
    valorOutros: Number(item.valorOutros?.replace(",", ".")) || undefined,

    // === IMPOSTOS (XML) ===
    valorIcms: Number(item.valorIcms?.replace(",", ".")) || undefined,
    valorIpi: Number(item.valorIpi?.replace(",", ".")) || undefined,
    valorPis: Number(item.valorPis?.replace(",", ".")) || undefined,
    valorCofins: Number(item.valorCofins?.replace(",", ".")) || undefined,
    valorTotTrib: Number(item.valorTotTrib?.replace(",", ".")) || undefined,
    bcIcms: Number(item.bcIcms?.replace(",", ".")) || undefined,
    aliqIcms: Number(item.aliqIcms?.replace(",", ".")) || undefined,
    aliqIpi: Number(item.aliqIpi?.replace(",", ".")) || undefined,
    aliqPis: Number(item.aliqPis?.replace(",", ".")) || undefined,
    aliqCofins: Number(item.aliqCofins?.replace(",", ".")) || undefined,
    cstPis: item.cstPis?.trim() || undefined,
    cstCofins: item.cstCofins?.trim() || undefined,

    // === DADOS XML (FORNECEDOR) ===
    codProdForn: item.codProduto?.trim() || "",
    descProdForn: item.descProduto?.trim() || "",
    origemProdForn: item.origem?.trim() || "",
    ncmProdForn: item.ncmsh?.trim() || "",
    umFornecedor: item.unidade?.trim() || "",
    itemNF: item.itemXml || (index + 1).toString().padStart(3, "0"),
    qtdeFornecedor: quantidadeOriginal, // ✅ Preserva quantidade original do XML

    // === DADOS PROTHEUS (CATÁLOGO) - VAZIOS PARA CONEXÃO POSTERIOR ===
    codProduto: "", // ✅ Vazio - será preenchido ao conectar com catálogo
    descricao: "", // ✅ Vazio - será preenchido ao conectar com catálogo
    origemProduto: "", // ✅ Vazio - será preenchido ao conectar com catálogo
    ncmProduto: "", // ✅ Vazio - será preenchido ao conectar com catálogo
    unidadeMedida: "", // ✅ Vazio - será preenchido ao conectar com catálogo
    grupoTributario: "", // ✅ Vazio - será preenchido ao conectar com catálogo
    tipoProduto: "", // ✅ Vazio - será preenchido ao conectar com catálogo

    // === DADOS DO PEDIDO - VAZIOS PARA CONEXÃO POSTERIOR ===
    pedidoCompra: "", // ✅ Vazio - será preenchido ao conectar com pedido
    itemPedido: "", // ✅ Vazio - será preenchido ao conectar com pedido

    // === CAMPOS DE CONVERSÃO - VAZIOS PARA CONEXÃO POSTERIOR ===
    fatorConversao: undefined, // ✅ Vazio - será calculado ao conectar
    tpFato: undefined, // ✅ Vazio - será calculado ao conectar

    // === STATUS - VAZIOS PARA CONEXÃO POSTERIOR ===
    isConnected: false, // ✅ Não conectado inicialmente
    isSelected: false, // ✅ Não selecionado inicialmente
    relevanceScore: undefined, // ✅ Vazio - será calculado se necessário
  };
}

// =================================================================
// TRATAMENTO DE ERROS
// =================================================================

/**
 * Classe simplificada para criar erros
 */
class ApiErrorFactory {
  static createFromResponse(response: any): ApiError {
    if (response?.error) {
      return {
        message: response.error,
        type: "API_ERROR",
        details: response,
      };
    }

    if (response?.jaExiste) {
      return {
        message: DUPLICATE_KEY_MESSAGE,
        type: "CHAVE_JA_EXISTE",
        code: "DUPLICATE_KEY",
        details: response,
      };
    }

    if (!response?.dadosNota && response?.warning) {
      return {
        message:
          response.warning ||
          "Não foi possível carregar os dados da nota fiscal",
        type: "DADOS_NAO_DISPONIVEIS",
        code: "NO_DATA",
        details: response,
      };
    }

    return {
      message: response?.message || "Erro desconhecido na API",
      type: "UNKNOWN_ERROR",
      details: response,
    };
  }

  static createFromFetchError(error: any): ApiError {
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      return {
        message: "Erro de conexão com o servidor",
        type: "NETWORK_ERROR",
        code: "FETCH_FAILED",
        details: error,
      };
    }

    if (error.name === "AbortError") {
      return {
        message: "Requisição cancelada",
        type: "REQUEST_CANCELLED",
        code: "ABORTED",
        details: error,
      };
    }

    return {
      message: error.message || "Erro na requisição",
      type: "REQUEST_ERROR",
      code: "UNKNOWN",
      details: error,
    };
  }
}

// =================================================================
// HOOKS TANSTACK
// =================================================================

export function useFetchXmlDetailsTanstack() {
  return useMutation<
    XmlDetailsResponse | null,
    ApiError,
    { chave: string; validarInclusao?: boolean }
  >({
    mutationFn: async ({
      chave: chaveDFe,
      validarInclusao = true,
    }): Promise<XmlDetailsResponse | null> => {
      if (!chaveDFe?.trim()) {
        const error: ApiError = {
          message: "Chave da NFe é obrigatória",
          type: "VALIDATION_ERROR",
          code: "MISSING_KEY",
        };
        throw error;
      }

      const chave = chaveDFe.trim();

      try {
        // Usa a nova API de validação que criamos
        const response = await fetch("/api/prenotas/validaXML", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-validar-inclusao": validarInclusao ? "true" : "false",
          },
          body: JSON.stringify({
            chaveNFe: chave,
            buscarDados: true,
            format_type: "JSON",
            impdanfe: false,
          }),
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = {
              error: `HTTP ${response.status}: ${response.statusText}`,
            };
          }

          const error: ApiError = {
            message: errorData.error || "Erro na busca do XML",
            type: "HTTP_ERROR",
            code: `HTTP_${response.status}`,
            details: errorData,
          };
          throw error;
        }

        const resultado = await response.json();

        // Se a chave já existe, não retorna dados
        if (resultado.jaExiste) {
          const error: ApiError = {
            message: DUPLICATE_KEY_MESSAGE,
            type: "CHAVE_JA_EXISTE",
            code: "DUPLICATE_KEY",
            details: resultado,
          };
          throw error;
        }

        // Se não conseguiu buscar dados, retorna null
        if (!resultado.dadosNota) {
          console.warn(
            "⚠️ [useFetchXmlDetailsTanstack] Nenhum dado encontrado para a chave:",
            chave
          );
          return null;
        }

        // Converte os dados do Arquivei para o formato esperado
        const xmlData = converterDadosArquiveiParaXmlDetails(
          resultado.dadosNota
        );
        if (!xmlData) {
          const error: ApiError = {
            message: "Erro ao converter dados do XML",
            type: "CONVERSION_ERROR",
            code: "DATA_CONVERSION_FAILED",
            details: resultado.dadosNota,
          };
          throw error;
        }

        return xmlData;
      } catch (error: any) {
        console.error("🔍 [useFetchXmlDetailsTanstack] Erro na busca do XML:", {
          message: error.message,
          errorType: error.constructor.name,
          chave,
        });

        // Se já é um ApiError, propaga
        if (error.type && error.message) {
          throw error;
        }

        // Converte erros genéricos para ApiError
        if (error.message?.includes("já foi dada entrada")) {
          const apiError: ApiError = {
            message: error.message,
            type: "CHAVE_JA_EXISTE",
            code: "DUPLICATE_KEY",
            details: error,
          };
          throw apiError;
        }

        if (error.message?.includes("não foi possível carregar")) {
          const apiError: ApiError = {
            message:
              "Documento ainda não foi liberado no SEFAZ. Tente novamente em alguns minutos.",
            type: "XML_NOT_RELEASED",
            code: "NOT_AVAILABLE",
            details: error,
          };
          throw apiError;
        }

        // Converte outros erros para ApiError
        const apiError = ApiErrorFactory.createFromFetchError(error);
        throw apiError;
      }
    },
  });
}

// =================================================================
// VALIDAÇÃO DE XML - NOVA API
// =================================================================

import { useQuery } from "@tanstack/react-query";

export function useValidaXml(chaveNFe: string) {
  return useQuery({
    queryKey: ["valida-xml-entrada", chaveNFe],
    queryFn: async (): Promise<ApiResponse<any>> => {
      if (!chaveNFe?.trim()) {
        return {
          success: false,
          error: {
            message: "Chave da NFe é obrigatória",
            type: "VALIDATION_ERROR",
            code: "MISSING_KEY",
          },
        };
      }

      try {
        const response = await fetch("/api/prenotas/validaXML", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chaveNFe: chaveNFe.trim(),
            buscarDados: true, // Busca dados no Arquivei
            format_type: "JSON",
            impdanfe: false,
          }),
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = {
              error: `HTTP ${response.status}: ${response.statusText}`,
            };
          }

          return {
            success: false,
            error: {
              message: errorData.error || "Erro na validação",
              type: "HTTP_ERROR",
              code: `HTTP_${response.status}`,
              details: errorData,
            },
          };
        }

        const resultado = await response.json();
        return {
          success: true,
          data: resultado,
        };
      } catch (error: any) {
        console.error("❌ [useValidaXml] Erro na validação:", error);

        return {
          success: false,
          error: ApiErrorFactory.createFromFetchError(error),
        };
      }
    },
    enabled: !!chaveNFe?.trim(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Função utilitária para validação imperativa
 */
export async function validaXmlEntrada(
  chaveNFe: string
): Promise<ApiResponse<any>> {
  if (!chaveNFe?.trim()) {
    return {
      success: false,
      error: {
        message: "Chave da NFe é obrigatória",
        type: "VALIDATION_ERROR",
        code: "MISSING_KEY",
      },
    };
  }

  try {
    const response = await fetch("/api/prenotas/validaXML", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chaveNFe: chaveNFe.trim(),
        buscarDados: true, // Busca dados no Arquivei
        format_type: "JSON",
        impdanfe: false,
      }),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      return {
        success: false,
        error: {
          message: errorData.error || "Erro na validação",
          type: "HTTP_ERROR",
          code: `HTTP_${response.status}`,
          details: errorData,
        },
      };
    }

    const resultado = await response.json();
    return {
      success: true,
      data: resultado,
    };
  } catch (error: any) {
    console.error("❌ [validaXmlEntrada] Erro na validação:", error);

    return {
      success: false,
      error: ApiErrorFactory.createFromFetchError(error),
    };
  }
}

/**
 * Hook para validação simples (apenas verificar se existe no banco)
 */
export function useValidaXmlSimples(chaveNFe: string) {
  return useQuery({
    queryKey: ["valida-xml-simples", chaveNFe],
    queryFn: async (): Promise<ApiResponse<any>> => {
      if (!chaveNFe?.trim()) {
        return {
          success: false,
          error: {
            message: "Chave da NFe é obrigatória",
            type: "VALIDATION_ERROR",
            code: "MISSING_KEY",
          },
        };
      }

      try {
        const response = await fetch("/api/prenotas/validaXML", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chaveNFe: chaveNFe.trim(),
            buscarDados: false, // Apenas valida, não busca dados
          }),
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = {
              error: `HTTP ${response.status}: ${response.statusText}`,
            };
          }

          return {
            success: false,
            error: {
              message: errorData.error || "Erro na validação",
              type: "HTTP_ERROR",
              code: `HTTP_${response.status}`,
              details: errorData,
            },
          };
        }

        const resultado = await response.json();
        return {
          success: true,
          data: resultado,
        };
      } catch (error: any) {
        console.error("❌ [useValidaXmlSimples] Erro na validação:", error);

        return {
          success: false,
          error: ApiErrorFactory.createFromFetchError(error),
        };
      }
    },
    enabled: !!chaveNFe?.trim(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para buscar dados do XML no Arquivei
 */
export function useBuscarDadosXml(chaveNFe: string) {
  return useQuery({
    queryKey: ["buscar-dados-xml", chaveNFe],
    queryFn: async (): Promise<ApiResponse<any>> => {
      if (!chaveNFe?.trim()) {
        return {
          success: false,
          error: {
            message: "Chave da NFe é obrigatória",
            type: "VALIDATION_ERROR",
            code: "MISSING_KEY",
          },
        };
      }

      try {
        const response = await fetch("/api/prenotas/validaXML", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chaveNFe: chaveNFe.trim(),
            buscarDados: true,
            format_type: "JSON",
            impdanfe: false,
          }),
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch {
            errorData = {
              error: `HTTP ${response.status}: ${response.statusText}`,
            };
          }

          return {
            success: false,
            error: {
              message: errorData.error || "Erro ao buscar dados",
              type: "HTTP_ERROR",
              code: `HTTP_${response.status}`,
              details: errorData,
            },
          };
        }

        const resultado = await response.json();

        // Se a chave já existe, não retorna dados
        if (resultado.jaExiste) {
          return {
            success: false,
            error: {
              message: DUPLICATE_KEY_MESSAGE,
              type: "CHAVE_JA_EXISTE",
              code: "DUPLICATE_KEY",
              details: resultado,
            },
          };
        }

        // Se não conseguiu buscar dados, retorna aviso
        if (!resultado.dadosNota) {
          return {
            success: true,
            data: null,
            warning: "Nenhum dado encontrado para esta chave",
          };
        }

        return {
          success: true,
          data: resultado.dadosNota,
        };
      } catch (error: any) {
        console.error("❌ [useBuscarDadosXml] Erro ao buscar dados:", error);

        return {
          success: false,
          error: ApiErrorFactory.createFromFetchError(error),
        };
      }
    },
    enabled: !!chaveNFe?.trim(),
    staleTime: 5 * 60 * 1000,
  });
}

// =================================================================
// DOWNLOAD DA DANFE
// =================================================================

/**
 * Função para baixar a DANFE do Arquivei
 */
export async function downloadDanfe(
  chaveNFe: string
): Promise<{ success: boolean; error?: string }> {
  if (!chaveNFe?.trim()) {
    return {
      success: false,
      error: "Chave da NFe é obrigatória para download da DANFE",
    };
  }

  try {
    const chave = chaveNFe.trim();

    // Monta a URL da API do Arquivei para download da DANFE
    // Usa o proxy interno configurado no next.config.ts: /api/apps/ -> http://172.16.99.187:3001/
    const arquiveiUrl = `/api/apps/arquivei?danfe=true&JSON&access_key=${chave}`;

    console.log("🔍 [downloadDanfe] Iniciando download da DANFE:", {
      chave,
      chaveLength: chave.length,
      url: arquiveiUrl,
    });

    // Faz a requisição para baixar a DANFE
    const response = await fetch(arquiveiUrl, {
      method: "GET",
      headers: {
        Accept: "application/pdf, application/json, */*",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ [downloadDanfe] Erro na resposta:", {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });

      return {
        success: false,
        error: `Erro ao baixar DANFE: ${response.status} ${response.statusText}`,
      };
    }

    // Lê a resposta como texto (pode ser JSON com base64 ou PDF direto)
    const responseText = await response.text();
    console.log("🔍 [downloadDanfe] Resposta recebida:", {
      contentType: response.headers.get("content-type"),
      status: response.status,
      responseLength: responseText.length,
      responsePreview: responseText.substring(0, 100) + "...",
    });

    // Verifica se a resposta é um PDF direto
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/pdf")) {
      // Cria um blob com o PDF
      const blob = await response.blob();
      console.log("🔍 [downloadDanfe] Tamanho do blob:", blob.size, "bytes");

      if (blob.size === 0) {
        return {
          success: false,
          error: "PDF vazio recebido do servidor",
        };
      }

      // Cria um link temporário para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `DANFE_${chave}.pdf`;

      // Adiciona o link ao DOM, clica e remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpa a URL temporária
      window.URL.revokeObjectURL(url);

      console.log("✅ [downloadDanfe] DANFE baixada com sucesso (PDF direto)");
      return { success: true };
    }

    // Tenta parsear como JSON (pode conter base64)
    try {
      const jsonResponse = JSON.parse(responseText);
      console.log("🔍 [downloadDanfe] Resposta JSON:", {
        hasPdfBase64: !!jsonResponse.pdfBase64,
        hasData: !!jsonResponse.data,
        hasBase64: !!jsonResponse.base64,
        hasPdf: !!jsonResponse.pdf,
        keys: Object.keys(jsonResponse),
      });

      // Procura por base64 na resposta
      let base64Data = null;
      if (jsonResponse.pdfBase64) {
        base64Data = jsonResponse.pdfBase64;
      } else if (jsonResponse.data) {
        base64Data = jsonResponse.data;
      } else if (jsonResponse.base64) {
        base64Data = jsonResponse.base64;
      } else if (jsonResponse.pdf) {
        base64Data = jsonResponse.pdf;
      } else if (typeof jsonResponse === "string") {
        // Se a resposta é uma string, pode ser base64 direto
        base64Data = jsonResponse;
      }

      if (base64Data) {
        console.log(
          "🔍 [downloadDanfe] Base64 encontrado, convertendo para PDF..."
        );

        // Remove prefixo data: se existir
        const cleanBase64 = base64Data.replace(
          /^data:application\/pdf;base64,/,
          ""
        );

        // Converte base64 para blob
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        console.log("🔍 [downloadDanfe] Blob criado:", {
          size: blob.size,
          type: blob.type,
        });

        if (blob.size === 0) {
          return {
            success: false,
            error: "PDF vazio após conversão do base64",
          };
        }

        // Cria um link temporário para download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `DANFE_${chave}.pdf`;

        // Adiciona o link ao DOM, clica e remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Limpa a URL temporária
        window.URL.revokeObjectURL(url);

        console.log(
          "✅ [downloadDanfe] DANFE baixada com sucesso (base64 convertido)"
        );
        return { success: true };
      } else {
        console.error(
          "❌ [downloadDanfe] Nenhum base64 encontrado na resposta:",
          jsonResponse
        );
        return {
          success: false,
          error:
            jsonResponse.error ||
            jsonResponse.message ||
            "Nenhum PDF encontrado na resposta",
        };
      }
    } catch (parseError) {
      console.error("❌ [downloadDanfe] Erro ao parsear resposta:", parseError);
      console.log("🔍 [downloadDanfe] Resposta original:", responseText);

      return {
        success: false,
        error: "Resposta inválida do servidor",
      };
    }
  } catch (error: any) {
    console.error("❌ [downloadDanfe] Erro no download:", error);

    return {
      success: false,
      error: error.message || "Erro desconhecido no download da DANFE",
    };
  }
}

/**
 * Hook para download da DANFE com estado de loading
 */
export function useDownloadDanfe() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(async (chaveNFe: string) => {
    if (!chaveNFe?.trim()) {
      setError("Chave da NFe é obrigatória");
      return false;
    }

    setIsDownloading(true);
    setError(null);

    try {
      const result = await downloadDanfe(chaveNFe);

      if (result.success) {
        setError(null);
        return true;
      } else {
        setError(result.error || "Erro no download");
        return false;
      }
    } catch (error: any) {
      const errorMessage = error.message || "Erro desconhecido no download";
      setError(errorMessage);
      return false;
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return {
    download,
    isDownloading,
    error,
  };
}

/**
 * Função para converter dados do Arquivei para o formato esperado pelo sistema
 */
export function converterDadosArquiveiParaXmlDetails(
  dadosArquivei: any
): XmlDetailsResponse | null {
  if (!dadosArquivei) return null;

  console.log(
    "🔍 [converterDadosArquiveiParaXmlDetails] Dados recebidos:",
    dadosArquivei
  );

  try {
    console.log(
      "🔍 [converterDadosArquiveiParaXmlDetails] Processando itens:",
      dadosArquivei.itens
    );

    const itens: XmlItem[] =
      dadosArquivei.itens?.map((item: any, index: number) => {
        console.log(
          `🔍 [converterDadosArquiveiParaXmlDetails] Processando item ${index}:`,
          item
        );
        return {
          codProduto: item.codProduto || "",
          descProduto: item.descProduto || "",
          ncmsh: item.ncmsh || "",
          origem: item.origem || "",
          cfop: item.cfop || "",
          unidade: item.unidade || "",
          quantidade: item.quantidade?.toString() || "0",
          valorTotal: item.valorTotal?.toString() || "0",
          itemXml: (index + 1).toString().padStart(3, "0"),

          // Valores detalhados
          valorUnitario: item.valorUnitario?.toString() || undefined,
          valorUnitarioTrib: item.valorUnitarioTrib?.toString() || undefined,
          valorDesconto: item.valorDesconto?.toString() || undefined,
          valorFrete: item.valorFrete?.toString() || undefined,
          valorSeguro: item.valorSeguro?.toString() || undefined,
          valorOutros: item.valorOutros?.toString() || undefined,

          // Impostos
          valorIcms: item.valorIcms?.toString() || undefined,
          valorIpi: item.valorIpi?.toString() || undefined,
          valorPis: item.valorPis?.toString() || undefined,
          valorCofins: item.valorCofins?.toString() || undefined,
          valorTotTrib: item.valorTotTrib?.toString() || undefined,
          bcIcms: item.bcIcms?.toString() || undefined,
          aliqIcms: item.aliqIcms?.toString() || undefined,
          aliqIpi: item.aliqIpi?.toString() || undefined,
          aliqPis: item.aliqPis?.toString() || undefined,
          aliqCofins: item.aliqCofins?.toString() || undefined,
          cstPis: item.cstPis || undefined,
          cstCofins: item.cstCofins || undefined,
        };
      }) || [];

    return {
      numero: dadosArquivei.numero || "",
      serie: dadosArquivei.serie || "",
      dataEmissao: dadosArquivei.dataEmissao || "",
      valorTotalDaNota: dadosArquivei.valorTotalDaNota?.toString() || "0",
      // ✅ TOTAIS DA NOTA FISCAL
      valorDesconto: dadosArquivei.valorDesconto?.toString() || "0",
      valorFrete: dadosArquivei.valorFrete?.toString() || "0",
      valorOutros: dadosArquivei.valorOutros?.toString() || "0",
      valorIcms: dadosArquivei.valorIcms?.toString() || "0",
      valorIpi: dadosArquivei.valorIpi?.toString() || "0",
      valorPis: dadosArquivei.valorPis?.toString() || "0",
      valorCofins: dadosArquivei.valorCofins?.toString() || "0",
      // Dados do emitente
      nomeEmitente: dadosArquivei.nomeEmitente || "",
      cnpjEmitente: dadosArquivei.cnpjEmitente || "",
      documentoEmitente: dadosArquivei.documentoEmitente || "",
      ufEmitente: dadosArquivei.ufEmitente || "",
      // Dados do destinatário
      nomeDestinatario: dadosArquivei.nomeDestinatario || "",
      cnpjDestinatario: dadosArquivei.cnpjDestinatario || "",
      documentoTomador: dadosArquivei.documentoTomador || "",
      ufDestinatario: dadosArquivei.ufDestinatario || "",
      // Informações adicionais
      informacoesAdicionais: dadosArquivei.informacoesAdicionais || "",
      nfeRelacionada: dadosArquivei.nfeRelacionada || "",
      cteComplementar: dadosArquivei.cteComplementar || "",
      itens: itens,
    } as XmlDetailsResponse;
  } catch (error) {
    console.error(
      "❌ [converterDadosArquiveiParaXmlDetails] Erro ao converter dados do Arquivei:",
      error
    );
    return null;
  }
}
